import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Link,
  Paper,
  Switch,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ThunkDispatch } from "@reduxjs/toolkit";

import { RootState } from "../../store/Store";
import {
  fetchData,
  putData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../store/actions";
import { useCustomToast } from "../../components/Methods/SnackBar";
import { setIsLoading } from "../../store/LoadingSlice";
import { projectsUrl } from "../../Service/Apis";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import PageTitle from "../../components/Page Component/PageTitle";
import { getDataGridPaginationProps } from "utils/paginationConfig";

interface Project {
  project_id: string;
  project_name: string;
  project_description: string;
  project_key: string;
  team_id: string;
  team_name: string;
  project_manager: string | null;
  manager_name: string | null;
  is_active?: boolean;
  created_on: string;
}

type StatusFilter = "active" | "inactive" | "all";

function buildProjectToolbar(statusFilter: StatusFilter, onStatusChange: (v: StatusFilter) => void) {
  return function ProjectToolbar() {
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          padding: "0.5rem",
          borderBottom: "1px solid rgba(224, 224, 224, 1)",
        }}
      >
        <FormControl
          size="small"
          variant="outlined"
          sx={{ minWidth: 100, maxWidth: 120 }}
        >
          <InputLabel id="project-status-filter-label">Status</InputLabel>
          <Select
            labelId="project-status-filter-label"
            value={statusFilter}
            label="Status"
            onChange={(e) =>
              onStatusChange(e.target.value as StatusFilter)
            }
            sx={{
              fontSize: "0.8125rem",
              height: 32,
              "& .MuiSelect-select": { py: 0.5, display: "flex", alignItems: "center" },
            }}
          >
            <MenuItem value="active" dense sx={{ fontSize: "0.8125rem" }}>
              Active
            </MenuItem>
            <MenuItem value="inactive" dense sx={{ fontSize: "0.8125rem" }}>
              Inactive
            </MenuItem>
            <MenuItem value="all" dense sx={{ fontSize: "0.8125rem" }}>
              All status
            </MenuItem>
          </Select>
        </FormControl>
        <GridToolbarQuickFilter sx={{ ml: "auto", margin: "0.5rem" }} />
      </GridToolbarContainer>
    );
  };
}

const ProjectManagement: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [toggleBusyId, setToggleBusyId] = useState<string | null>(null);

  const filteredProjects = useMemo(() => {
    if (statusFilter === "all") {
      return projects;
    }
    if (statusFilter === "active") {
      return projects.filter((p) => p.is_active !== false);
    }
    return projects.filter((p) => p.is_active === false);
  }, [projects, statusFilter]);

  const ToolbarComponent = React.useMemo(
    () => buildProjectToolbar(statusFilter, setStatusFilter),
    [statusFilter]
  );

  const showToast = useCustomToast();
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );
  const canEditProjects =
    selectedRole === "Admin" || selectedRole === "HR";

  useEffect(() => {
    if (selectedRole !== "Admin" && selectedRole !== "HR") {
      navigate("/landing", { replace: true });
    }
  }, [selectedRole, navigate]);

  useEffect(() => {
    if (selectedRole !== "Admin" && selectedRole !== "HR") {
      return;
    }
    fetchProjects();
  }, [selectedRole]);

  const fetchProjects = async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await dispatch2(
        fetchData(
          `${projectsUrl}?role=${selectedRole}&include_inactive=true`
        )
      );
      const rows = (response as Project[]).map((p) => ({
        ...p,
        is_active: p.is_active !== false,
      }));
      setProjects(rows);
    } catch (error) {
      console.error("Error fetching projects:", error);
      showToast("Error fetching projects", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleIsActiveChange = async (project: Project, checked: boolean) => {
    if (!canEditProjects) {
      return;
    }
    const prev = projects.map((p) => ({ ...p }));
    setProjects((list) =>
      list.map((p) =>
        p.project_id === project.project_id ? { ...p, is_active: checked } : p
      )
    );
    setToggleBusyId(project.project_id);
    try {
      await dispatch2(
        putData(
          `${projectsUrl}${project.project_id}/?role=${selectedRole}`,
          { is_active: checked }
        )
      );
      showToast("Project updated", "success", true);
    } catch {
      setProjects(prev);
    } finally {
      setToggleBusyId(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "project_name",
      headerName: "Project Name",
      flex: 1,
      renderCell: (params) => (
        <Link
          component={RouterLink}
          to={`/projects/${params.row.project_id}`}
          underline="hover"
          color="inherit"
          onClick={(e) => e.stopPropagation()}
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: "project_key",
      headerName: "Project code",
      width: 130,
    },
    {
      field: "is_active",
      headerName: "Active",
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Switch
            size="small"
            checked={params.row.is_active !== false}
            disabled={
              !canEditProjects || toggleBusyId === params.row.project_id
            }
            onChange={(_, checked) => handleIsActiveChange(params.row, checked)}
          />
        </Box>
      ),
    },
    // {
    //   field: "created_on",
    //   headerName: "Created",
    //   width: 180,
    //   valueFormatter: (params) => {
    //     if (!params.value) return "";
    //     return new Date(params.value).toLocaleDateString("en-GB");
    //   },
    // },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Box sx={{ mb: 3 }}>
            <PageTitle>Project Management</PageTitle>
          </Box>
          <Typography variant="body1" color="black">
            View and manage projects synced from Jira.
          </Typography>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
        <DataGrid
          rows={filteredProjects}
          columns={columns}
          getRowId={(row) => row.project_id}
          autoHeight
          {...getDataGridPaginationProps()}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
            },
          }}
          slots={{
            toolbar: ToolbarComponent,
            noRowsOverlay: CustomNoRowsOverlay,
            noResultsOverlay: CustomNoRowsOverlay,
          }}
        />
      </Paper>
    </Container>
  );
};

export default ProjectManagement;
